<template>
  <component
    :is="component"
    ref="comp"
    v-model="localModelValue"
    v-bind="componentProps"
  >
    <template #activator="slotData">
      <slot v-if="$slots.activator" name="activator" v-bind="slotData || {}" />
    </template>

    <v-card color="neutral25" flat class="rounded-lg">
      <slot v-if="!isDesktop" name="sheet-prepend">
        <h4
          v-if="sheetTitle"
          class="text-text text-h5 font-weight-semibold px-4 py-3"
          v-text="sheetTitle"
        />
      </slot>
      <slot />
    </v-card>
  </component>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed, ref, watch, useSlots } from 'vue'
import { useDisplay } from 'vuetify'
import { VDialog } from 'vuetify/components'
import { useBackButton } from '@/composables/useBackButton'

// Access slots to check if they exist
const $slots = useSlots()

// Props definition
const props = defineProps({
  modelValue: Boolean,
  sheetTitle: {
    type: String,
    default: null,
  },
  dialogProps: {
    type: Object,
    default: () => ({}),
  },
  sheetProps: {
    type: Object,
    default: () => ({}),
  },
})

// Emits definition
const emit = defineEmits(['update:modelValue'])

// Setup refs
const comp = ref(null)

// Setup v-model binding
const localModelValue = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value)
})

// Setup display breakpoints
const { mdAndUp } = useDisplay()
const isDesktop = computed(() => mdAndUp.value)

// Determine which component to use based on viewport
const component = computed(() => {
  if (isDesktop.value) return VDialog
  return 'bottom-sheet-draggable'
})

// Setup component props based on component type
const componentProps = computed(() => {
  if (isDesktop.value) {
    return {
      ...props.dialogProps,
    }
  }

  return {
    inset: true,
    ...props.sheetProps,
  }
})

// Setup back button handling
const close = () => {
  localModelValue.value = false
}

const onBackButtonPressed = () => {
  const compValue = comp.value

  if (compValue?.onClose) {
    compValue.onClose()
  } else if (compValue?.closeConditional) {
    compValue.closeConditional()
  } else if (!componentProps.value.persistent) {
    close()
  }
}

const { setupBackButtonDetector, destroyBackButtonDetector } = useBackButton(onBackButtonPressed)

// Watch for dialog open/close to setup back button detection
watch(() => props.modelValue, open => {
  if (open) {
    setupBackButtonDetector()
  } else {
    destroyBackButtonDetector()
  }
}, { immediate: true })

// Expose methods to parent component
defineExpose({
  close
})
/* eslint-enable no-undef */
</script>

<script>
export default {
  inheritAttrs: false
}
</script>
